import type { CSSProperties } from "@emotion/serialize";
import { palette, shades } from "lib/miolo";
import { getYear, lightFormat } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import orderBy from "lodash/orderBy";
import tinycolor from "tinycolor2";

interface IOfficersProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

export default function Officers({ issuer }: IOfficersProps) {
  const sortedOfficers = orderBy(issuer.officers, ["board", "name"], ["desc", "asc"]);

  const officers = [
    ...sortedOfficers.filter((officer) => {
      const isCEO = issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name;
      return isCEO;
    }),
    ...sortedOfficers.filter((officer) => {
      const isCEO = issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name;
      return !isCEO;
    }),
  ];

  return (
    <div
      css={{
        padding: 8,
        overflow: "hidden",
        backgroundColor: palette.white,
        borderRadius: 4,
      }}
    >
      <table
        css={{
          minWidth: "100%",
          fontSize: 14,
          fontFamily: "museo-sans-condensed",
          lineHeight: 1.33,
          letterSpacing: 0.6,
          textAlign: "left",
          textIndent: 0,
          borderColor: "inherit",
          borderCollapse: "collapse",
          "& > thead": {
            backgroundColor: palette.white,
          },
          "& th": {
            padding: "12px 16px",
            color: shades.greys.variants[2],
            fontWeight: 600,
            textTransform: "uppercase",
          },
          "& tr > td": {
            padding: "12px 16px",
          },
          "& > tbody": {
            "& tr": {
              borderColor: palette.fiord,
              borderTopWidth: 1,
              "&:hover": {
                backgroundColor: tinycolor(palette.catskillWhite).lighten(2).toHexString(),
                "& td:first-of-type": {
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                },
                "& td:last-of-type": {
                  borderTopRightRadius: 6,
                  borderBottomRightRadius: 6,
                },
              },
            },
          },
          "& tbody > tr": {
            borderTop: `solid 1px ${tinycolor(palette.mystic).lighten(3).toHexString()}`,
            borderBottom: "none",
            "&:hover, &:hover + tr": {
              borderTop: "solid 1px transparent",
            },
          },
        }}
      >
        <thead>
          <tr>
            <th>Nome</th>
            <th>Quadro</th>
            <th>Mandato</th>
          </tr>
        </thead>

        <tbody>
          {officers.map((officer, index) => {
            const yearOfBirth = getYear(parseDateYmd(officer.dateOfBirth));
            const age = getYear(new Date()) - yearOfBirth;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${officer.name}/${index}`}>
                <td>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div>
                        <span
                          css={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: shades.greys.variants[2],
                          }}
                        >
                          {officer.name}
                        </span>
                      </div>

                      <div>
                        <span css={{ fontSize: 14, color: shades.greys.variants[3], letterSpacing: 1 }}>
                          {officer.position} • {age} anos
                        </span>
                      </div>
                    </div>

                    {issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name && (
                      <div css={{ marginLeft: 12 }}>
                        <span
                          css={{
                            padding: "4px 12px",
                            color: palette.cobalt,
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: 1,
                            backgroundColor: palette.lilyWhite,
                            borderRadius: 12,
                          }}
                        >
                          CEO
                        </span>
                      </div>
                    )}
                  </div>
                </td>

                <td>
                  <span
                    css={{
                      padding: "4px 12px",
                      fontWeight: 500,
                      letterSpacing: 1,
                      backgroundColor: palette.catskillWhite,
                      borderRadius: 12,
                      ...getBoardTagCSS(officer.board),
                    }}
                  >
                    {getCleanBoard(officer.board)}
                  </span>
                </td>

                <td>
                  <div css={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                    <span
                      title={officer.termOfOffice}
                      css={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: shades.greys.variants[3],
                      }}
                    >
                      {officer.termOfOffice}
                    </span>
                  </div>

                  <div>
                    <span css={{ fontSize: 14, color: shades.greys.variants[3], letterSpacing: 1 }}>
                      {"Eleito em "}
                      <time dateTime={officer.electedDate}>
                        {lightFormat(parseDateYmd(officer.electedDate), "dd/MM/yyyy")}
                      </time>
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getBoardTagCSS(board: string): CSSProperties {
  if (board === "Pertence apenas à Diretoria") {
    return {
      backgroundColor: palette.grannyApple,
      color: palette.stromboli,
    };
  }
  if (board === "Pertence apenas ao Conselho de Administração") {
    return {
      backgroundColor: palette.selago,
      color: palette.royalBlue,
    };
  }
  return {
    backgroundColor: palette.catskillWhite,
    color: palette.blueBayoux,
  };
}

function getCleanBoard(board: string) {
  if (board === "Pertence apenas à Diretoria") {
    return "Diretoria";
  }
  if (board === "Pertence apenas ao Conselho de Administração") {
    return "Conselho de Administração";
  }
  return "Conselho Fiscal";
}
